import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from 'theme/withStyles';
import RawHtml from 'common/RawHtml';
import { connect } from 'react-redux';
import workFlowActions from 'core/workflow/actions';
import dialogActions from 'core/dialogs/actions';
import pageActions from 'core/page/actions';
import classnames from 'classnames';
import iconCaretDown from 'icons/icon-caret-down.svg';
import ErrorMsg from 'common/ErrorMsg';
import buttonLinkStyles from 'styles/buttonLink';
import AppRequestHeader from './HomeAppRequestHeader';
import FundingVerificationPanel from './HomeFundingVerificationPanel';
import DocumentsPanel from './HomeDocumentsPanel';
import DisclosuresPanel from './HomeDisclosuresPanel';
import AgreementsPanel from './HomeAgreementsPanel';
import ServicesPanel from './HomeServicesPanel';
import HomeProductDisplayGroup from './HomeProductDisplayGroup';

const styles = ({ links, screenSizes, spacing, typography }) => ({
  aside: {
    '& a': {
      '&:hover': {
        textDecoration: 'underline',
      },
      [`@media(max-width: ${screenSizes.mobileMax}px)`]: {
        color: ({ showMobileLogo }) =>
          showMobileLogo ? links.link.color : typography.progress.color,
      },
      color: typography.progress.color,
      textDecoration: 'none',
    },
    color: typography.progress.color,
    fontSize: 18,
    padding: [0, spacing.xs],
    position: 'absolute',
    right: 0,
    top: -75,
    [`@media(max-width: ${screenSizes.mobileMax}px)`]: {
      color: ({ showMobileLogo }) =>
        showMobileLogo ? typography.p3.color : typography.progress.color,
      fontSize: 16,
      top: ({ showMobileLogo }) => (showMobileLogo ? 0 : -40),
    },
  },
  buttonLink: buttonLinkStyles({ links }),
  delimeter: {
    background: typography.progress.color,
    display: 'inline-block',
    height: 15,
    position: 'relative',
    top: 2,
    width: 1,
    [`@media(max-width: ${screenSizes.mobileMax}px)`]: {
      background: ({ showMobileLogo }) =>
        showMobileLogo ? typography.p3.color : typography.progress.color,
    },
  },
  footer: {
    marginTop: spacing.lg,
  },
  h1: {
    ...typography.h1,
    fontSize: 24,
    fontWeight: 400,
    marginBottom: 5,
  },
  iconCaretDown: {
    background: `url(${iconCaretDown}) 100% 3px no-repeat`,
    display: 'inline-block',
    height: 16,
    width: 16,
  },
  logout: {
    ...buttonLinkStyles({ links }),
    '&:hover': {
      color: typography.progress.color,
    },
    [`@media(max-width: ${screenSizes.mobileMax}px)`]: {
      color: links.link.color,
    },
    color: typography.progress.color,
    fontSize: typography.p3.fontSize,
    fontWeight: typography.fontWeights.bold,
    paddingLeft: 15,
    whiteSpace: 'nowrap',
  },
  needHelp: {
    fontSize: typography.p3.fontSize,
    paddingRight: 15,
  },
  root: {
    '& article': {
      textAlign: 'left',
    },
    margin: [0, 'auto'],
    maxWidth: 900,
    textAlign: 'center',
    [`@media(max-width: ${screenSizes.mobileMax}px)`]: {
      paddingTop: 50,
    },
  },
});

export const HomeBare = ({
  appRequests,
  classes,
  disableButton,
  footerHelpLabel,
  headerHtml,
  logout,
  openDialog,
  resources,
  restart,
}) => (
  <div className={classnames('home-view', classes.root)}>
    <RawHtml>{headerHtml}</RawHtml>

    <div className={classnames('aside', classes.aside)}>
      <span className={classnames('need-help', classes.needHelp)}>
        <RawHtml type="span">{resources.needhelpLabel}</RawHtml>
      </span>
      {resources.logoutLinkLabel && (
        <>
          <span className={classnames('delimeter', classes.delimeter)} />
          <button
            type="button"
            className={classnames('buttonLink logout', classes.logout)}
            onClick={(e) => {
              e.preventDefault();
              logout(resources.logoutRedirectUrl);
            }}
          >
            {resources.logoutLinkLabel}
          </button>
        </>
      )}
    </div>
    <br />
    {resources.ssoMaxInstanceMessage && (
      <ErrorMsg error errorMsg={resources.ssoMaxInstanceMessage} type="page" />
    )}
    <section>
      {(!appRequests || appRequests.length === 0) && <HomeProductDisplayGroup />}
      {appRequests?.map((request) => (
        <article key={request.id} data-id={request.id}>
          <div role="tablist">
            <AppRequestHeader
              request={request}
              resources={resources}
              restart={restart}
              disableButton={disableButton}
            />
            {request.fundingVerificationId && request.fundingVerificationPending && (
              <FundingVerificationPanel
                request={request}
                resources={resources}
                openDialog={openDialog}
              />
            )}
            {request.tabs?.includes('Documents') && (
              <DocumentsPanel request={request} resources={resources} openDialog={openDialog} />
            )}
            {request.tabs?.includes('Disclosures') && (
              <DisclosuresPanel request={request} resources={resources} openDialog={openDialog} />
            )}
            {request.tabs?.includes('Agreements') && (
              <AgreementsPanel request={request} resources={resources} openDialog={openDialog} />
            )}
          </div>
        </article>
      ))}
      <article>
        <ServicesPanel resources={resources} openDialog={openDialog} />
      </article>
    </section>
    <div className={classnames('homeFooter', classes.footer)}>
      <RawHtml>{footerHelpLabel}</RawHtml>
    </div>
  </div>
);

HomeBare.propTypes = {
  appRequests: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  disableButton: PropTypes.bool.isRequired,
  footerHelpLabel: PropTypes.string,
  headerHtml: PropTypes.node,
  logout: PropTypes.func.isRequired,
  openDialog: PropTypes.func.isRequired,
  resources: PropTypes.shape({
    applyLinkLabel: PropTypes.string,
    logoutLinkLabel: PropTypes.string,
    logoutRedirectUrl: PropTypes.string,
    needhelpLabel: PropTypes.string,
    ssoMaxInstanceMessage: PropTypes.string,
  }).isRequired,

  restart: PropTypes.func.isRequired,
  // eslint-disable-next-line react/no-unused-prop-types
  showMobileLogo: PropTypes.bool,
};

HomeBare.defaultProps = {
  footerHelpLabel: '',
  headerHtml: '',
  showMobileLogo: false,
};

const mapStateToProps = ({ data, page, resources, session }) => ({
  ...page,
  appRequests: data.appRequests,
  footerHelpLabel: resources.footerHelpLabel,
  productDisplayGroup: data.productDisplayGroup,
  resources,
  showMobileLogo: session.meta?.configs?.mobileLogo,
});

const mapDispatchToProps = {
  logout: (redirectUrl) => pageActions.logout(redirectUrl),
  openDialog: dialogActions.open,
  restart: workFlowActions.restart,
};

const HomeStyled = withStyles(styles)(HomeBare);

export default connect(mapStateToProps, mapDispatchToProps)(HomeStyled);
